import React, { useEffect, useMemo, useState } from "react";
import { AppLayout } from "../components/app/AppLayout";
import { auditApi } from "../lib/api";
import {
  Ban, CheckCircle2, ChevronRight, Loader2, Printer, Filter, X, Building2,
  Users as UsersIcon, Briefcase, CreditCard, Upload as UploadIcon, Sparkles, Shield,
} from "lucide-react";

/** Human-readable label + icon for each entity_type value. */
const ENTITY_META = {
  facility: { label: "Facility", icon: Building2, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  user:     { label: "User",     icon: UsersIcon, color: "text-blue-700 bg-blue-50 border-blue-200" },
  company:  { label: "Company",  icon: Briefcase, color: "text-purple-700 bg-purple-50 border-purple-200" },
  plan:     { label: "Plan",     icon: CreditCard, color: "text-amber-700 bg-amber-50 border-amber-200" },
  import:   { label: "Import",   icon: UploadIcon, color: "text-sky-700 bg-sky-50 border-sky-200" },
  ai:       { label: "AI",       icon: Sparkles,  color: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  role:     { label: "Role",     icon: Shield,    color: "text-slate-700 bg-slate-100 border-slate-300" },
  other:    { label: "Other",    icon: Filter,    color: "text-slate-600 bg-slate-50 border-slate-200" },
};

const humanizeAction = (a) => (a || "").replace(/_/g, " ").toLowerCase().replace(/(^|\s)\w/g, (m) => m.toUpperCase());

const EntityChip = ({ type }) => {
  const meta = ENTITY_META[type] || ENTITY_META.other;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
      <Icon size={10} strokeWidth={2.5} />
      {meta.label}
    </span>
  );
};

const StatusChange = ({ old_status, new_status }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider">
    {new_status === "SUSPENDED"
      ? <Ban size={13} className="text-red-600" strokeWidth={2.5} />
      : <CheckCircle2 size={13} className="text-emerald-600" strokeWidth={2.5} />}
    <span>{old_status}</span>
    <ChevronRight size={11} />
    <span className={new_status === "SUSPENDED" ? "text-red-700" : "text-emerald-700"}>{new_status}</span>
  </span>
);

function DetailsCell({ row }) {
  // Facility status change → old→new arrow. Everything else: humanized action + details keys.
  if (row.old_status && row.new_status) {
    return (
      <>
        <StatusChange old_status={row.old_status} new_status={row.new_status} />
        {row.reason && <div className="mt-1 text-xs text-slate-700">{row.reason}</div>}
      </>
    );
  }
  const details = row.details || {};
  return (
    <div className="text-xs text-slate-700">
      <div className="font-semibold">{humanizeAction(row.action_type)}</div>
      {row.entity_label && <div className="text-slate-600">{row.entity_label}</div>}
      {Object.keys(details).length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-[10px] font-semibold text-nmi-700 hover:text-nmi-800">
            details
          </summary>
          <pre className="mt-1 overflow-x-auto rounded-sm bg-slate-50 p-1.5 text-[10px] leading-tight text-slate-700">
            {JSON.stringify(details, null, 2)}
          </pre>
        </details>
      )}
      {row.reason && <div className="mt-1 text-slate-600">{row.reason}</div>}
    </div>
  );
}

const ENTITY_OPTIONS = ["facility", "user", "role", "company", "plan", "import", "ai"];

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters
  const [entityTypes, setEntityTypes] = useState(new Set()); // empty = ALL
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(200);
  // Summary counts
  const [summary, setSummary] = useState(null);

  const query = useMemo(() => {
    const q = { limit };
    if (entityTypes.size > 0) q.entity_type = Array.from(entityTypes).join(",");
    if (since) q.since = new Date(since).toISOString();
    if (until) {
      // inclusive upper bound — treat date-only as end-of-day
      const d = new Date(until);
      if (/^\d{4}-\d{2}-\d{2}$/.test(until)) d.setHours(23, 59, 59, 999);
      q.until = d.toISOString();
    }
    return q;
  }, [entityTypes, since, until, limit]);

  useEffect(() => {
    setLoading(true);
    auditApi.list(query).then((r) => setItems(r.items || [])).finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { auditApi.summary().then(setSummary).catch(() => {}); }, []);

  const toggleEntity = (t) => setEntityTypes((prev) => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });

  const clearFilters = () => {
    setEntityTypes(new Set()); setSince(""); setUntil(""); setLimit(200);
  };

  const doPrint = () => window.print();

  return (
    <AppLayout mobileTitle="Audit Log">
      <div className="mb-4 print:mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Immutable ledger</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Audit Log</h1>
          <p className="mt-1 text-sm text-slate-600">Every mutation — facilities, users, roles, companies, plans, imports and AI edits — is permanently recorded here.</p>
        </div>
        <button
          onClick={doPrint}
          data-testid="audit-print-btn"
          className="print:hidden inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800 whitespace-nowrap"
        >
          <Printer size={13} strokeWidth={2.5} /> Print
        </button>
      </div>

      {/* Filters */}
      <div className="print:hidden mb-4 rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            <Filter size={11} /> Filters
          </div>
          {(entityTypes.size || since || until) && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-red-700" data-testid="audit-clear-filters">
              <X size={10} /> Clear
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {ENTITY_OPTIONS.map((t) => {
                const on = entityTypes.has(t);
                const meta = ENTITY_META[t];
                const Icon = meta.icon;
                return (
                  <button
                    key={t}
                    onClick={() => toggleEntity(t)}
                    data-testid={`audit-filter-${t}`}
                    className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      on ? "border-nmi-700 bg-nmi-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-nmi-600 hover:text-nmi-700"
                    }`}
                  >
                    <Icon size={11} strokeWidth={2.5} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">From</label>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              data-testid="audit-since"
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">To</label>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              data-testid="audit-until"
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Rows</label>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} data-testid="audit-limit" className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600">
              {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Nationwide HSP Network Manager · Audit Log</div>
        <div className="text-[10px] text-slate-500">
          Printed {new Date().toLocaleString()}
          {entityTypes.size > 0 && <> · Types: {Array.from(entityTypes).join(", ")}</>}
          {since && <> · From {since}</>}
          {until && <> · To {until}</>}
          {" · "}Showing {items.length} rows
        </div>
      </div>

      {/* Summary row */}
      {summary && (
        <div className="mb-3 flex flex-wrap gap-2 text-[11px] text-slate-600 print:hidden">
          <span className="rounded-sm bg-slate-100 px-2 py-0.5"><b>{items.length}</b> rows loaded</span>
          <span className="rounded-sm bg-slate-100 px-2 py-0.5"><b>{(summary.entity_types || []).length}</b> entity types tracked</span>
          <span className="rounded-sm bg-slate-100 px-2 py-0.5"><b>{(summary.action_types || []).length}</b> action types</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-slate-200 print:border-0">
        <table className="w-full border-collapse text-sm" data-testid="audit-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 print:bg-white">
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 whitespace-nowrap">When</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Type</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Entity</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Details</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Actor</th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Channel</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-10 text-center text-slate-500"><Loader2 size={18} className="inline animate-spin" /> Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500">No audit entries match these filters.</td></tr>
            )}
            {!loading && items.map((a) => {
              const t = a.entity_type || (a.facility_id ? "facility" : "other");
              return (
                <tr key={a.audit_id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 print:hover:bg-white" data-testid={`audit-row-${a.audit_id}`}>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600 whitespace-nowrap align-top">{a.timestamp?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2.5 align-top"><EntityChip type={t} /></td>
                  <td className="px-3 py-2.5 align-top">
                    {a.facility_name || a.entity_label || "—"}
                    {(a.facility_id || a.entity_id) && (
                      <div className="text-[10px] font-mono text-slate-400 truncate max-w-[240px]">{a.facility_id || a.entity_id}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 max-w-md align-top"><DetailsCell row={a} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-800 align-top whitespace-nowrap">{a.actor_name}</td>
                  <td className="px-3 py-2.5 align-top">
                    <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">{a.channel || "web"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
